import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Bookmark, Trash2, Copy, Loader2, Calendar, FileText, Anchor, BarChart3, Star, Target, Zap, MousePointerClick, BookOpen } from 'lucide-react';
import { copyToClipboard } from '../utils/clipboard';

// Helper component to render script beats
const ScriptBeats = ({ beats }) => {
  if (!beats || beats.length === 0) return null;
  return (
    <div className="border-t border-slate-700/50 pt-3 mt-3 space-y-2">
      <p className="text-xs text-slate-500 uppercase tracking-wider">Script Beats</p>
      {beats.map((b, i) => (
        <div key={i} className="bg-slate-800/50 p-2 rounded border-l-2 border-slate-600">
          <p className="text-xs text-slate-500 font-medium">{b.name}</p>
          <p className="text-sm text-slate-300">{b.content}</p>
        </div>
      ))}
    </div>
  );
};

// Helper component to render a mini score bar
const MiniScoreBar = ({ label, score, icon: Icon }) => {
  const color = score >= 80 ? 'text-emerald-400' : score >= 60 ? 'text-amber-400' : 'text-red-400';
  const bgColor = score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <Icon className={`w-3 h-3 ${color}`} />
      <span className="text-xs text-slate-400 w-16 truncate">{label}</span>
      <div className="flex-1 h-1.5 bg-slate-700/50 rounded-full">
        <div className={`h-1.5 rounded-full ${bgColor}`} style={{ width: `${score}%` }} />
      </div>
      <span className={`text-xs font-semibold ${color}`}>{score}</span>
    </div>
  );
};

// Helper component to render saved analysis content
const SavedAnalysisContent = ({ content }) => {
  if (!content) return null;
  const { input, scores, titleVariations, improvedDescription, betterHook, ctaRecommendation, maxExtras } = content;
  
  return (
    <div className="space-y-4">
      {/* Input Summary */}
      <div className="flex flex-wrap gap-2">
        {input?.platform && (
          <Badge className="bg-slate-700 text-slate-300 text-xs">{input.platform}</Badge>
        )}
        {input?.niche && (
          <Badge className="bg-slate-700 text-slate-300 text-xs">{input.niche}</Badge>
        )}
      </div>
      
      {/* Scores Grid */}
      {scores && (
        <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/40 space-y-2">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Scores</p>
          <MiniScoreBar label="Hook" score={scores.hookStrength} icon={Zap} />
          <MiniScoreBar label="SEO" score={scores.seoScore} icon={Target} />
          <MiniScoreBar label="CTR" score={scores.clickThroughPotential} icon={MousePointerClick} />
          <MiniScoreBar label="Read" score={scores.readability} icon={BookOpen} />
          <div className="pt-1 border-t border-slate-700/50">
            <MiniScoreBar label="Overall" score={scores.overall} icon={Star} />
          </div>
        </div>
      )}
      
      {/* Title Variations */}
      {titleVariations && titleVariations.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Rewritten Titles</p>
          {titleVariations.map((t, i) => (
            <p key={i} className="text-sm text-slate-300 pl-3 border-l-2 border-violet-500/30">{t}</p>
          ))}
        </div>
      )}
      
      {/* Improvements */}
      {(improvedDescription || betterHook) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {improvedDescription && (
            <div className="bg-slate-900/50 rounded-lg p-3 border border-emerald-500/20">
              <p className="text-xs text-emerald-400 font-semibold mb-1">Improved Description</p>
              <p className="text-sm text-slate-300 line-clamp-3">{improvedDescription}</p>
            </div>
          )}
          {betterHook && (
            <div className="bg-slate-900/50 rounded-lg p-3 border border-amber-500/20">
              <p className="text-xs text-amber-400 font-semibold mb-1">Better Hook</p>
              <p className="text-sm text-slate-300 line-clamp-3">{betterHook}</p>
            </div>
          )}
        </div>
      )}
      
      {/* CTA */}
      {ctaRecommendation && (
        <div className="bg-slate-900/50 rounded-lg p-3 border border-violet-500/20">
          <p className="text-xs text-violet-400 font-semibold mb-1">CTA Recommendation</p>
          <p className="text-sm text-slate-300">{ctaRecommendation}</p>
        </div>
      )}
      
      {/* Max Extras */}
      {maxExtras && (
        <div className="space-y-2">
          <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 text-xs">MAX INSIGHTS</Badge>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {maxExtras.viralPotential && (
              <div className="bg-slate-900/50 rounded p-2 border border-cyan-500/20">
                <p className="text-xs text-cyan-400 font-semibold">Viral Potential</p>
                <p className="text-xs text-slate-300 line-clamp-2">{maxExtras.viralPotential}</p>
              </div>
            )}
            {maxExtras.audienceProfile && (
              <div className="bg-slate-900/50 rounded p-2 border border-cyan-500/20">
                <p className="text-xs text-cyan-400 font-semibold">Audience</p>
                <p className="text-xs text-slate-300 line-clamp-2">{maxExtras.audienceProfile}</p>
              </div>
            )}
            {maxExtras.trendingAnalysis && (
              <div className="bg-slate-900/50 rounded p-2 border border-cyan-500/20">
                <p className="text-xs text-cyan-400 font-semibold">Trending</p>
                <p className="text-xs text-slate-300 line-clamp-2">{maxExtras.trendingAnalysis}</p>
              </div>
            )}
            {maxExtras.bestAngleSuggestion && (
              <div className="bg-slate-900/50 rounded p-2 border border-cyan-500/20">
                <p className="text-xs text-cyan-400 font-semibold">Best Angle</p>
                <p className="text-xs text-slate-300 line-clamp-2">{maxExtras.bestAngleSuggestion}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SavedContent = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [activeTab, setActiveTab] = useState('idea');

  useEffect(() => {
    fetchSavedContent();
  }, []);

  const fetchSavedContent = async () => {
    try {
      const res = await api.get('/saved');
      setContent(res.data);
    } catch (err) {
      toast.error('Failed to load saved content');
    } finally {
      setLoading(false);
    }
  };

  const deleteContent = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/saved/${id}`);
      setContent(prev => prev.filter(c => c.id !== id));
      toast.success('Content deleted');
    } catch (err) {
      toast.error('Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  // Helper to get planner data (handles both old and new format)
  const getPlannerData = (item) => {
    if (!item || item.type !== 'planner') return null;
    // New format: data in content wrapper
    if (item.content && typeof item.content === 'object') {
      return {
        platform: item.content.platform,
        niche: item.content.niche,
        days: item.content.days || [],
        frequencyAtGeneration: item.content.frequencyAtGeneration,
        totalPostsGenerated: item.content.totalPostsGenerated
      };
    }
    // Old format: data at top level (posts instead of days)
    if (item.platform) {
      return {
        platform: item.platform,
        niche: item.niche,
        days: item.posts || [],
        frequencyAtGeneration: item.frequencyAtGeneration,
        totalPostsGenerated: item.totalPostsGenerated
      };
    }
    return null;
  };

  const copyContent = (item) => {
    let text = '';
    if (item.type === 'idea') {
      text = `${item.content.title}\n\n${item.content.description}`;
    } else if (item.type === 'script') {
      // Copy full script with all beats
      text = `${item.title}\n\n${item.content?.script || ''}\n\n`;
      if (item.content?.beats?.length > 0) {
        text += '--- BEATS ---\n\n';
        item.content.beats.forEach((beat) => {
          text += `[${beat.name}]\n${beat.content}\n\n`;
        });
      }
    } else if (item.type === 'hook') {
      text = item.content.hook || item.title;
    } else if (item.type === 'planner') {
      // Copy planner using helper (handles old/new format)
      const plannerInfo = getPlannerData(item);
      if (plannerInfo) {
        const days = plannerInfo.days || [];
        text = `30-Day Plan: ${plannerInfo.niche || 'Unknown'}\nPlatform: ${plannerInfo.platform || 'Unknown'}\n\n` +
          days.slice(0, 5).map(d => d ? `Day ${d.day}: ${d.title}` : '').filter(Boolean).join('\n') +
          (days.length > 5 ? `\n... and ${days.length - 5} more days` : '');
      }
    } else if (item.type === 'analysis') {
      // Copy full analysis
      const c = item.content;
      text = `CONTENT ANALYSIS: ${item.title}\n\n`;
      text += `=== INPUT ===\n`;
      text += `Title: ${c.input?.title || 'N/A'}\n`;
      text += `Description: ${c.input?.description || 'N/A'}\n`;
      text += `Platform: ${c.input?.platform || 'N/A'}\n`;
      text += `Niche: ${c.input?.niche || 'N/A'}\n\n`;
      text += `=== SCORES ===\n`;
      if (c.scores) {
        text += `Hook Strength: ${c.scores.hookStrength}/100\n`;
        text += `SEO Score: ${c.scores.seoScore}/100\n`;
        text += `Click-Through Potential: ${c.scores.clickThroughPotential}/100\n`;
        text += `Readability: ${c.scores.readability}/100\n`;
        text += `Overall: ${c.scores.overall}/100\n\n`;
      }
      text += `=== TITLE VARIATIONS ===\n`;
      if (c.titleVariations) {
        c.titleVariations.forEach((t, i) => { text += `${i + 1}. ${t}\n`; });
      }
      text += `\n=== IMPROVED DESCRIPTION ===\n${c.improvedDescription || 'N/A'}\n\n`;
      text += `=== BETTER HOOK ===\n${c.betterHook || 'N/A'}\n\n`;
      text += `=== CTA RECOMMENDATION ===\n${c.ctaRecommendation || 'N/A'}\n`;
      if (c.maxExtras) {
        text += `\n=== MAX INSIGHTS ===\n`;
        text += `Viral Potential: ${c.maxExtras.viralPotential || 'N/A'}\n`;
        text += `Retention Prediction: ${c.maxExtras.retentionPrediction || 'N/A'}\n`;
        text += `Audience Profile: ${c.maxExtras.audienceProfile || 'N/A'}\n`;
        text += `Competitor Gap: ${c.maxExtras.competitorGapAnalysis || 'N/A'}\n`;
        text += `Trending Analysis: ${c.maxExtras.trendingAnalysis || 'N/A'}\n`;
        text += `Best Angle: ${c.maxExtras.bestAngleSuggestion || 'N/A'}\n`;
      }
    }
    copyToClipboard(text);
    toast.success('Copied to clipboard!');
  };

  const tabs = [
    { id: 'idea', label: 'Ideas', icon: Bookmark },
    { id: 'hook', label: 'Hooks', icon: Anchor },
    { id: 'script', label: 'Scripts', icon: FileText },
    { id: 'analysis', label: 'Analyses', icon: BarChart3 },
    { id: 'planner', label: 'Planners', icon: Calendar }
  ];

  const filteredContent = content
    .filter(item => item && item.type === activeTab)
    .filter(item => {
      // Extra safety for planners: ensure we can extract valid data
      if (item.type === 'planner') {
        const data = getPlannerData(item);
        return data && data.platform;
      }
      return true;
    });

  const getEmptyMessage = () => {
    if (activeTab === 'idea') return 'Save ideas from the Idea Generator to build your content library.';
    if (activeTab === 'hook') return 'No saved hooks yet. Generate and save hooks from Writing Tools.';
    if (activeTab === 'script') return 'No saved scripts yet. Generate and save scripts from Script Builder.';
    if (activeTab === 'planner') return 'No saved planners yet. Generate a 30-Day Plan to see it here.';
    if (activeTab === 'analysis') return 'No saved analyses yet. Run an AI Content Analysis and save it to see it here.';
    return 'No saved content yet.';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="saved-content">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold tracking-tight mb-2 bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
          Saved Content
        </h1>
        <p className="text-slate-400 text-lg">
          Your personal library of generated ideas, hooks, scripts, and planners.
        </p>
      </div>

      {/* Tabs */}
      <div className="inline-flex bg-slate-800/50 rounded-lg p-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-2 rounded-md transition-all flex items-center gap-2 ${
                activeTab === tab.id 
                  ? 'bg-slate-700 text-white' 
                  : 'bg-transparent text-slate-400 hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </Button>
          );
        })}
      </div>

      {/* Content */}
      {filteredContent.length === 0 ? (
        <div className="border-2 border-dashed border-slate-700/50 rounded-xl p-12 text-center">
          <h3 className="text-xl font-semibold text-white mb-2">
            No saved {activeTab}s yet
          </h3>
          <p className="text-slate-400">
            {getEmptyMessage()}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredContent.map((item) => (
            <Card 
              key={item.id} 
              className={`bg-slate-800/30 border-slate-700/50 ${item.type === 'planner' ? 'cursor-pointer hover:border-cyan-500/50 transition-colors' : ''}`}
              data-testid="saved-item"
              onClick={() => {
                if (item.type === 'planner') {
                  navigate(`/planner?savedId=${item.id}`);
                }
              }}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`${
                        item.type === 'planner' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30' :
                        item.type === 'script' ? 'bg-violet-500/10 text-violet-400 border-violet-500/30' :
                        item.type === 'analysis' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                        'bg-slate-700 text-slate-300'
                      }`}>
                        {item.type}
                      </Badge>
                      <span className="text-sm text-slate-500">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                    
                    {item.type === 'idea' && item.content.description && (
                      <p className="text-sm text-slate-400 line-clamp-2">
                        {item.content.description}
                      </p>
                    )}
                    {item.type === 'script' && (
                      <div className="space-y-3">
                        <p className="text-sm text-slate-400 whitespace-pre-wrap">
                          {item.content?.script || 'No script content'}
                        </p>
                        <ScriptBeats beats={item.content?.beats} />
                      </div>
                    )}
                    {item.type === 'hook' && (
                      <p className="text-sm text-slate-400">
                        {item.content.hook || item.title}
                      </p>
                    )}
                    {item.type === 'analysis' && (
                      <SavedAnalysisContent content={item.content} />
                    )}
                    {item.type === 'planner' && (() => {
                      const plannerInfo = getPlannerData(item);
                      if (!plannerInfo) return null;
                      return (
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            {plannerInfo.platform && (
                              <Badge className="bg-slate-700 text-slate-300 text-xs">
                                {plannerInfo.platform}
                              </Badge>
                            )}
                            <Badge className="bg-slate-700 text-slate-300 text-xs">
                              {plannerInfo.days?.length || 0} days
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-400">
                            {plannerInfo.days?.slice(0, 3).map(d => d?.title).filter(Boolean).join(' | ')}
                            {plannerInfo.days?.length > 3 && '...'}
                          </p>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="flex gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); copyContent(item); }}
                      className="text-slate-400 hover:text-white"
                      data-testid="copy-saved-btn"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); deleteContent(item.id); }}
                      disabled={deleting === item.id}
                      className="text-slate-400 hover:text-red-400"
                      data-testid="delete-saved-btn"
                    >
                      {deleting === item.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedContent;
